import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { AxiosResponse } from "axios";
import { errorToastMessage, toastMessage } from "../../utils/toast";
import { QRCodeSVG } from "qrcode.react";
import { DownloadIcon, PrintIcon } from "../Common/assets/Icons";
import { commonContainerWrapper } from "../Common/styles/flex";
import http from "../../utils/http";
import { CopyContentIcon } from "./Icons";

type Props = {
  studyId?: string;
};
const FlyerCard = ({ studyId }: Props) => {
  const baseUrl = import.meta.env.REACT_APP_PATIENT_PORTAL || "";

  const [data, setData] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const [downloadLoader, setDownloadLoader] = useState(false);
  const [printLoader, setPrintLoader] = useState(false);
  const qrRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        setLoading(true);
        let url = `/study/${studyId}`;
        const res: AxiosResponse = await http.get(url);
        const data = res.data?.data;
        setData(data || {});
        setLoading(false);
      } catch (err) {
        setLoading(false);
        errorToastMessage(err as Error);
      }
    };
    if (studyId) {
      fetchDetails();
    }
  }, [setLoading, studyId]);

  const downloadQRCode = () => {
    try {
      setDownloadLoader(true);

      if (!qrRef.current) return;

      const svg = qrRef.current;
      const svgData = new XMLSerializer().serializeToString(svg);
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      const img = new Image();
      const svgBlob = new Blob([svgData], {
        type: "image/svg+xml;charset=utf-8",
      });
      const url = URL.createObjectURL(svgBlob);

      img.onload = () => {
        canvas.width = img.width;
        canvas.height = img.height;
        ctx?.drawImage(img, 0, 0);
        URL.revokeObjectURL(url);

        const pngUrl = canvas.toDataURL("image/png");
        const link = document.createElement("a");
        link.href = pngUrl;
        link.download = "qr-code.png";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        setDownloadLoader(false);
      };

      img.src = url;
    } catch (err) {
      errorToastMessage(err as Error);
      setDownloadLoader(false);
    }
  };
  const printFlyer = async () => {
    try {
      setPrintLoader(true);
      let res = await http.get(`/admin/study/${studyId}/print/flyer`);
      const downloadLink = res?.data?.data?.fileUrl;
      window.open(downloadLink, "_blank");
      toastMessage("success", res.data.message);
      setPrintLoader(false);
    } catch (err) {
      setPrintLoader(false);
      errorToastMessage(err as Error);
    }
  };

  return (
    <Box sx={{ ...commonContainerWrapper, p: 2, minHeight: "200px" }}>
      <Typography variant="h2" color="text.primary">
        Flyer Invitation
      </Typography>
      {!loading ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 3,
          }}
        >
          <Typography
            variant="subtitle1"
            fontWeight={"regular"}
            color="text.primary"
          >
            Share this QR code or link with participants to join this{" "}
            {studyId ? "study" : "registry"}
          </Typography>
          <Box>
            <QRCodeSVG
              ref={qrRef}
              value={studyId ? baseUrl + "study/" + data?.slug : baseUrl}
              size={160}
              title="Study QR Code"
            />

            <Typography
              variant="body1"
              fontWeight={"regular"}
              color="text.primary"
              textAlign={"center"}
            >
              Scan to join {studyId ? "study" : "registry"}
            </Typography>
          </Box>

          <TextField
            id="study-url"
            placeholder="Study URL"
            value={studyId ? baseUrl + "study/" + data?.slug : baseUrl}
            sx={{
              width: "500px",
            }}
            InputProps={{
              endAdornment: (
                <IconButton
                  edge="end"
                  onClick={() => {
                    navigator.clipboard.writeText(
                      studyId ? baseUrl + "study/" + data?.slug : baseUrl
                    );
                    toastMessage("success", "Copied!");
                  }}
                >
                  <CopyContentIcon />
                </IconButton>
              ),
              readOnly: true,
            }}
          />

          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
            {downloadLoader ? (
              <CircularProgress size={25} />
            ) : (
              <Button
                variant="outlined"
                onClick={downloadQRCode}
                startIcon={<DownloadIcon />}
              >
                Download QR
              </Button>
            )}
            {/* {printLoader ? (
              <CircularProgress size={25} />
            ) : (
              <Button
                variant="outlined"
                onClick={printFlyer}
                startIcon={<PrintIcon />}
              >
                Print Flyer
              </Button>
            )} */}
          </Box>
        </Box>
      ) : (
        <Box sx={{ display: "flex", justifyContent: "center", mt: "5%" }}>
          <CircularProgress size={25} />
        </Box>
      )}
    </Box>
  );
};

export default FlyerCard;
